import type { AnchorSide, DiagramEdge, DiagramNode, Point } from './types';

export interface RoutedEdge {
  path: string;
  points: Point[];
  labelPoint: Point;
  sourceAnchor: Exclude<AnchorSide, 'auto'>;
  targetAnchor: Exclude<AnchorSide, 'auto'>;
}

interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const ROUTE_MARGIN = 18;
const OBSTACLE_MARGIN = 12;
const EPSILON = 0.0001;

export function centerOf(node: DiagramNode): Point {
  return { x: node.x + node.width / 2, y: node.y + node.height / 2 };
}

function rotateVector(vector: Point, degrees: number): Point {
  if (!degrees) return vector;
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { x: vector.x * cos - vector.y * sin, y: vector.x * sin + vector.y * cos };
}

export function rotatePoint(point: Point, center: Point, degrees: number): Point {
  const rotated = rotateVector({ x: point.x - center.x, y: point.y - center.y }, degrees);
  return { x: center.x + rotated.x, y: center.y + rotated.y };
}

export function localAnchorPoint(width: number, height: number, side: Exclude<AnchorSide, 'auto'>): Point {
  switch (side) {
    case 'topLeft': return { x: width * 0.25, y: 0 };
    case 'top': return { x: width * 0.5, y: 0 };
    case 'topRight': return { x: width * 0.75, y: 0 };
    case 'rightTop': return { x: width, y: height * 0.25 };
    case 'right': return { x: width, y: height * 0.5 };
    case 'rightBottom': return { x: width, y: height * 0.75 };
    case 'bottomRight': return { x: width * 0.75, y: height };
    case 'bottom': return { x: width * 0.5, y: height };
    case 'bottomLeft': return { x: width * 0.25, y: height };
    case 'leftBottom': return { x: 0, y: height * 0.75 };
    case 'left': return { x: 0, y: height * 0.5 };
    case 'leftTop': return { x: 0, y: height * 0.25 };
  }
}

export function anchorPoint(node: DiagramNode, side: Exclude<AnchorSide, 'auto'>): Point {
  const local = localAnchorPoint(node.width, node.height, side);
  const point = { x: node.x + local.x, y: node.y + local.y };
  return rotatePoint(point, centerOf(node), node.rotation || 0);
}

export function chooseAnchor(node: DiagramNode, target: DiagramNode): Exclude<AnchorSide, 'auto'> {
  const center = centerOf(node);
  const targetCenter = centerOf(target);
  const localDelta = rotateVector({ x: targetCenter.x - center.x, y: targetCenter.y - center.y }, -(node.rotation || 0));
  if (Math.abs(localDelta.x) >= Math.abs(localDelta.y)) return localDelta.x >= 0 ? 'right' : 'left';
  return localDelta.y >= 0 ? 'bottom' : 'top';
}

function cardinalDirectionForAnchor(node: DiagramNode, side: Exclude<AnchorSide, 'auto'>): Point {
  const localDirection: Point = side.startsWith('top')
    ? { x: 0, y: -1 }
    : side.startsWith('right')
      ? { x: 1, y: 0 }
      : side.startsWith('bottom')
        ? { x: 0, y: 1 }
        : { x: -1, y: 0 };
  const rotated = rotateVector(localDirection, node.rotation || 0);
  if (Math.abs(rotated.x) >= Math.abs(rotated.y)) return { x: rotated.x >= 0 ? 1 : -1, y: 0 };
  return { x: 0, y: rotated.y >= 0 ? 1 : -1 };
}

export function nodeBounds(node: DiagramNode, margin = 0): Rect {
  const center = centerOf(node);
  const corners = [
    { x: node.x, y: node.y },
    { x: node.x + node.width, y: node.y },
    { x: node.x + node.width, y: node.y + node.height },
    { x: node.x, y: node.y + node.height },
  ].map((point) => rotatePoint(point, center, node.rotation || 0));
  return {
    left: Math.min(...corners.map((point) => point.x)) - margin,
    right: Math.max(...corners.map((point) => point.x)) + margin,
    top: Math.min(...corners.map((point) => point.y)) - margin,
    bottom: Math.max(...corners.map((point) => point.y)) + margin,
  };
}

function expandedRect(node: DiagramNode, margin = OBSTACLE_MARGIN): Rect {
  return nodeBounds(node, margin);
}

function nearlyEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON;
}

function segmentHitsRect(a: Point, b: Point, rect: Rect): boolean {
  if (nearlyEqual(a.x, b.x)) {
    if (a.x <= rect.left || a.x >= rect.right) return false;
    const min = Math.min(a.y, b.y);
    const max = Math.max(a.y, b.y);
    return max > rect.top && min < rect.bottom;
  }
  if (nearlyEqual(a.y, b.y)) {
    if (a.y <= rect.top || a.y >= rect.bottom) return false;
    const min = Math.min(a.x, b.x);
    const max = Math.max(a.x, b.x);
    return max > rect.left && min < rect.right;
  }
  // Segmentos diagonais só aparecem em conexões retas. O roteador ortogonal
  // mantém seus segmentos alinhados aos eixos, então não precisamos usar esta
  // aproximação para pontuar rotas internas.
  return false;
}

function cleanPoints(points: Point[]): Point[] {
  const result: Point[] = [];
  for (const point of points) {
    const previous = result[result.length - 1];
    if (previous && nearlyEqual(previous.x, point.x) && nearlyEqual(previous.y, point.y)) continue;
    result.push(point);
    while (result.length >= 3) {
      const a = result[result.length - 3];
      const b = result[result.length - 2];
      const c = result[result.length - 1];
      if ((nearlyEqual(a.x, b.x) && nearlyEqual(b.x, c.x)) || (nearlyEqual(a.y, b.y) && nearlyEqual(b.y, c.y))) result.splice(result.length - 2, 1);
      else break;
    }
  }
  return result;
}

function scoreRoute(points: Point[], obstacles: Rect[]): number {
  let score = 0;
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index];
    const b = points[index + 1];
    score += Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    for (const obstacle of obstacles) {
      if (segmentHitsRect(a, b, obstacle)) score += 1_000_000;
    }
  }
  score += Math.max(0, points.length - 2) * 10;
  return score;
}

function routeMiddle(start: Point, end: Point, obstacles: Rect[]): Point[] {
  if ((nearlyEqual(start.x, end.x) || nearlyEqual(start.y, end.y)) && !obstacles.some((obstacle) => segmentHitsRect(start, end, obstacle))) return [start, end];

  const candidates: Point[][] = [
    [start, { x: end.x, y: start.y }, end],
    [start, { x: start.x, y: end.y }, end],
  ];
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  candidates.push(
    [start, { x: midX, y: start.y }, { x: midX, y: end.y }, end],
    [start, { x: start.x, y: midY }, { x: end.x, y: midY }, end],
  );

  const xLanes = new Set<number>();
  const yLanes = new Set<number>();
  for (const obstacle of obstacles) {
    xLanes.add(obstacle.left - ROUTE_MARGIN);
    xLanes.add(obstacle.right + ROUTE_MARGIN);
    yLanes.add(obstacle.top - ROUTE_MARGIN);
    yLanes.add(obstacle.bottom + ROUTE_MARGIN);
  }
  for (const x of xLanes) candidates.push([start, { x, y: start.y }, { x, y: end.y }, end]);
  for (const y of yLanes) candidates.push([start, { x: start.x, y }, { x: end.x, y }, end]);

  return candidates
    .map(cleanPoints)
    .sort((a, b) => scoreRoute(a, obstacles) - scoreRoute(b, obstacles))[0];
}

function pathFromPoints(points: Point[]): string {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function pointAlongPath(points: Point[], ratio = 0.5): Point {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];
  const lengths = points.slice(1).map((point, index) => Math.abs(point.x - points[index].x) + Math.abs(point.y - points[index].y));
  const total = lengths.reduce((sum, value) => sum + value, 0);
  if (total === 0) return points[0];
  let target = total * ratio;
  for (let index = 0; index < lengths.length; index += 1) {
    if (target <= lengths[index]) {
      const a = points[index];
      const b = points[index + 1];
      const t = lengths[index] === 0 ? 0 : target / lengths[index];
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
    target -= lengths[index];
  }
  return points[points.length - 1];
}


function pointAlongPathWithSegment(points: Point[], ratio: number): { point: Point; a: Point; b: Point } {
  if (points.length < 2) {
    const point = points[0] ?? { x: 0, y: 0 };
    return { point, a: point, b: point };
  }
  const lengths = points.slice(1).map((point, index) => Math.abs(point.x - points[index].x) + Math.abs(point.y - points[index].y));
  const total = lengths.reduce((sum, value) => sum + value, 0);
  if (total <= EPSILON) return { point: points[0], a: points[0], b: points[1] };
  let target = total * ratio;
  for (let index = 0; index < lengths.length; index += 1) {
    const length = lengths[index];
    if (target <= length || index === lengths.length - 1) {
      const a = points[index];
      const b = points[index + 1];
      const t = length <= EPSILON ? 0 : Math.max(0, Math.min(1, target / length));
      return { point: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }, a, b };
    }
    target -= length;
  }
  return { point: points[points.length - 1], a: points[points.length - 2], b: points[points.length - 1] };
}

function labelPointForEdge(edge: DiagramEdge, points: Point[], source: DiagramNode, target: DiagramNode): Point {
  if (!edge.label?.trim()) return pointAlongPath(points);
  // Rótulos de ramificação ficam um pouco antes do meio e fora da linha. Além
  // de melhorar a leitura, isso evita que duas saídas de uma decisão empilhem
  // caixas de "Sim"/"Não" no mesmo trecho.
  const sourceCenter = centerOf(source);
  const targetCenter = centerOf(target);
  const located = pointAlongPathWithSegment(points, 0.38);
  const horizontal = Math.abs(located.b.x - located.a.x) >= Math.abs(located.b.y - located.a.y);
  const offset = 18;
  if (horizontal) {
    const direction = targetCenter.y < sourceCenter.y - EPSILON ? -1 : 1;
    return { x: located.point.x, y: located.point.y + direction * offset };
  }
  const direction = targetCenter.x < sourceCenter.x - EPSILON ? -1 : 1;
  return { x: located.point.x + direction * offset, y: located.point.y };
}

export function routeEdge(edge: DiagramEdge, source: DiagramNode, target: DiagramNode, allNodes: DiagramNode[] = []): RoutedEdge {
  const sourceAnchor = edge.sourceAnchor && edge.sourceAnchor !== 'auto' ? edge.sourceAnchor : chooseAnchor(source, target);
  const targetAnchor = edge.targetAnchor && edge.targetAnchor !== 'auto' ? edge.targetAnchor : chooseAnchor(target, source);
  const start = anchorPoint(source, sourceAnchor);
  const end = anchorPoint(target, targetAnchor);

  if (edge.routingMode === 'manual' && edge.waypoints && edge.waypoints.length > 0) {
    const points = cleanPoints([start, ...edge.waypoints, end]);
    return { path: pathFromPoints(points), points, labelPoint: labelPointForEdge(edge, points, source, target), sourceAnchor, targetAnchor };
  }

  if (edge.style === 'straight') {
    const points = [start, end];
    return { path: pathFromPoints(points), points, labelPoint: labelPointForEdge(edge, points, source, target), sourceAnchor, targetAnchor };
  }

  const startDirection = cardinalDirectionForAnchor(source, sourceAnchor);
  const endDirection = cardinalDirectionForAnchor(target, targetAnchor);
  const startStub = { x: start.x + startDirection.x * ROUTE_MARGIN, y: start.y + startDirection.y * ROUTE_MARGIN };
  const endStub = { x: end.x + endDirection.x * ROUTE_MARGIN, y: end.y + endDirection.y * ROUTE_MARGIN };
  const obstacles = allNodes
    .filter((node) => node.id !== source.id && node.id !== target.id && !node.hidden)
    .map((node) => expandedRect(node));
  const middle = routeMiddle(startStub, endStub, obstacles);
  const points = cleanPoints([start, ...middle, end]);
  return { path: pathFromPoints(points), points, labelPoint: labelPointForEdge(edge, points, source, target), sourceAnchor, targetAnchor };
}

// Compatibilidade com o R1 e integrações que ainda chamam estas funções.
export function orthogonalPath(source: DiagramNode, target: DiagramNode): string {
  const edge = { sourceAnchor: 'auto', targetAnchor: 'auto', style: 'orthogonal' } as DiagramEdge;
  return routeEdge(edge, source, target).path;
}

export function straightPath(source: DiagramNode, target: DiagramNode): string {
  const edge = { sourceAnchor: 'auto', targetAnchor: 'auto', style: 'straight' } as DiagramEdge;
  return routeEdge(edge, source, target).path;
}
